import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileAlt,
  faTools,
  faHandshake,
  faShieldAlt,
  faGavel,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import KnowledgeCenter from './KnowledgeCenter';
import './Resources.scss';

/**
 * Resources component - Displays information about MET Connect®'s resources and documentation
 * 
 * @returns {JSX.Element} The Resources component
 */
const Resources = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="resources-page">
      <div className="hero-section" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.672), rgba(134, 133, 133, 0.2)), url(${process.env.PUBLIC_URL}/images/blueprints.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Resources
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                Access guides, documentation, and industry insights to support your implementation.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faFileAlt} className="feature-icon" />
                  <span>Detailed Documentation</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faTools} className="feature-icon" />
                  <span>Troubleshooting Resources</span>
                </div>
              </div>
              <div className="hero-buttons">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="animated-btn me-3"
                  onClick={() => {
                    const knowledgeCenter = document.querySelector('.knowledge-center-section');
                    knowledgeCenter?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span className="animated-btn-content">
                    Knowledge Center
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </span>
                </Button>
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="animated-btn"
                  onClick={() => {
                    const policyCenter = document.querySelector('.policy-center-section');
                    policyCenter?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span className="animated-btn-content">
                    Policy Center
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </span>
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="resources-overview">
        <Container>
              <div className="content-block">
                <h2 className="section-title text-center mb-4">Resource Center</h2>
                <p>
                  Welcome to the MET Connect® resource center. Here you'll find a collection of technical documentation,
                  guides, tutorials, and other resources to help you get the most out of our solutions.
                </p>
                <p>
                  Our Knowledge Center provides articles and guides to help you implement and maximize your MET Connect® 
                  systems. Explore our documentation, best practices, and troubleshooting guides below.
                </p>
              </div>
        </Container>
      </section>
      
      <section className="knowledge-center-section">
        <KnowledgeCenter />
      </section>

      <section className="policy-center-section py-5">
        <Container>
          <h2 className="section-title text-center mb-4">Policy Center</h2>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 policy-card">
                <Card.Body className="d-flex flex-column">
                  <div className="policy-icon mb-3">
                    <FontAwesomeIcon icon={faHandshake} />
                  </div>
                  <Card.Title>Service Provider Agreement</Card.Title>
                  <Card.Text>
                    Terms and conditions governing the use of MET Connect® services and solutions.
                  </Card.Text>
                  <div className="mt-auto">
                    <a href="https://met-connect.com/spa/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View Agreement</a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 policy-card">
                <Card.Body className="d-flex flex-column">
                  <div className="policy-icon mb-3">
                    <FontAwesomeIcon icon={faShieldAlt} />
                  </div>
                  <Card.Title>Privacy Policy</Card.Title>
                  <Card.Text>
                    Our commitment to protecting your privacy and how we handle your data.
                  </Card.Text>
                  <div className="mt-auto">
                    <a href={process.env.PUBLIC_URL + "/policy/PRIVACY-STATEMENT.pdf"} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View Policy</a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 policy-card">
                <Card.Body className="d-flex flex-column">
                  <div className="policy-icon mb-3">
                    <FontAwesomeIcon icon={faGavel} />
                  </div>
                  <Card.Title>Legal Notices</Card.Title>
                  <Card.Text>
                    Important legal information, copyright notices, and trademark information.
                  </Card.Text>
                  <div className="mt-auto">
                    <a href={process.env.PUBLIC_URL + "/policy/LEGAL-NOTICES.pdf"} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View Notices</a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Resources;